import { callApi, IApiErrorResponse } from '@/api/callApi';
import { API_ENDPOINT, API_URL } from '@/api/constant';
import FormButtons from '@/components/common/Form/FormButtons';
import FormHead from '@/components/common/Form/FormHead';
import CardLayout from '@/components/Layout/CardLayout';
import Layout from '@/components/Layout/Layout';
import BackButton from '@/components/ui/BackButton';
import PrimaryInput from '@/components/ui/PrimaryInput';
import { showToastNotification } from '@/utility/helper';
import { errorTypes } from '@/utility/validation';
import moment from 'moment';
import { useEffect, useState } from 'react';
import { SubmitHandler, useForm } from 'react-hook-form';
import { useLocation, useNavigate } from 'react-router-dom';
interface IAddLeaveForm {
  leaveType: string;
  applicableFrom: string;
  totalLeavePerMonth: number;
  totalLeavePerYear: number;
  yearlyCarryForward: boolean;
  monthlyCarryForward: boolean;
}
interface IAddLeaveState {
  MonthValue: number | null;
}
interface IAddLeaveResponse {
  status: boolean;
  message: string;
}

const AddLeaveSetting = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [disabled, setDisabled] = useState<boolean>(true);
  const [state, setState] = useState<IAddLeaveState>({
    MonthValue: null,
  });
  const {
    register,
    handleSubmit,
    formState: { errors },
    setValue,
    watch,
    reset,
  } = useForm<IAddLeaveForm>();

  useEffect(() => {
    if (location?.state?.EditData) {
      setValue('leaveType', location?.state?.EditData?.leaveType);
      setValue(
        'applicableFrom',
        moment(location?.state?.EditData?.applicableFrom).format('YYYY-MM-DD'),
      );
      setValue(
        'totalLeavePerMonth',
        location?.state?.EditData?.totalLeavePerMonth,
      );
      setValue(
        'totalLeavePerYear',
        location?.state?.EditData?.totalLeavePerYear,
      );
      setValue(
        'yearlyCarryForward',
        location?.state?.EditData?.yearCarryForward,
      );
      setValue(
        'monthlyCarryForward',
        location?.state?.EditData?.monthlyCarryForward,
      );
      setDisabled(false);
      setState((pre) => ({
        ...pre,
        MonthValue: location?.state?.EditData?.totalLeavePerYear,
      }));
    } else {
      reset();
    }
  }, []);

  const totalLeavePerMonth = watch('totalLeavePerMonth');
  const onSubmit: SubmitHandler<IAddLeaveForm> = async (data) => {
    try {
      const postData = {
        type: data?.leaveType,
        applicableFrom: data?.applicableFrom,
        totalLeavePerMonth: data?.totalLeavePerMonth,
        totalLeavePerYear: data?.totalLeavePerYear,
        yearCarryForward: data?.yearlyCarryForward,
        monthlyCarryForward: data?.monthlyCarryForward,
      };
      setIsLoading(true);
      const state = location?.state;
      const API = state?.EditData
        ? API_URL +
          API_ENDPOINT?.updateLeaveSetting +
          `?leaveId=${state?.EditData?.id}`
        : API_URL + API_ENDPOINT?.addLeaveSetting;
      const response: IAddLeaveResponse = await callApi(API, {
        method: 'POST',
        data: postData,
      });
      setIsLoading(false);
      if (response?.status === true) {
        showToastNotification('success', response?.message);
        navigate(-1);
        reset();
      } else {
        showToastNotification('danger', response?.message);
      }
    } catch (error) {
      if (error && typeof error === 'object' && 'message' in error) {
        showToastNotification('danger', (error as IApiErrorResponse).message);
      } else {
        showToastNotification('danger', 'Something went wrong');
      }
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <>
      <Layout>
        <div className="content container-fluid">
          <BackButton
            label={`Leave Configuration`}
            isEdit={location?.state?.EditData}
          />
          <form onSubmit={handleSubmit(onSubmit)}>
            <CardLayout>
              <div className="tab-content" id="pills-tabContent">
                <div
                  className="tab-pane fade show active"
                  id="pills-BasicDetails"
                  role="tabpanel"
                  aria-labelledby="pills-BasicDetails-tab"
                  tabIndex={0}
                >
                  <div className="row">
                    <FormHead heading="Normal Details" />
                    <div className="col-12 col-md-6 col-lg-4">
                      <div className="form-group mb-3">
                        <PrimaryInput
                          label="Leave Type"
                          id="leaveType"
                          placeholder="Enter leave type"
                          {...register('leaveType', {
                            ...errorTypes.required('Leave Type'),
                            pattern: {
                              value: /^[a-zA-Z\s]*$/,
                              message:
                                'Leave type must contain only letters and spaces',
                            },
                          })}
                          errors={errors?.leaveType?.message}
                        />
                      </div>
                    </div>

                    <div className="col-12 col-md-6 col-lg-4">
                      <div className="form-group mb-3">
                        <PrimaryInput
                          type="date"
                          label="Applicable From"
                          {...register('applicableFrom', {
                            required: 'Applicable From date is required',
                            ...errorTypes.noSunday,
                          })}
                          errors={errors?.applicableFrom?.message}
                          id={'applicableFrom'}
                        />
                      </div>
                    </div>
                    <div className="col-12 col-md-6 col-lg-4">
                      <div className="form-group mb-3">
                        <PrimaryInput
                          label="Total Leave Per Month"
                          // type="number"
                          id="totalLeavePerMonth"
                          placeholder="Enter total leave per month"
                          type="text"
                          inputMode="numeric"
                          {...register('totalLeavePerMonth', {
                            ...errorTypes.required(),
                            valueAsNumber: true,
                          })}
                          onInput={(e) => {
                            e.currentTarget.value =
                              e.currentTarget.value.replace(/\D/g, '');
                          }}
                          min={1}
                          errors={errors?.totalLeavePerMonth?.message}
                          onChange={(e) => {
                            const monthlyLeave = parseFloat(
                              e.target.value || '0',
                            );
                            if (monthlyLeave > 0) {
                              setDisabled(true);
                              setValue('totalLeavePerYear', monthlyLeave * 12);
                              setState((pre) => ({
                                ...pre,
                                MonthValue: monthlyLeave * 12,
                              }));
                            } else {
                              setValue('totalLeavePerYear', 0);
                              setDisabled(true);
                            }
                          }}
                        />
                      </div>
                    </div>
                    {/* Total Leave Per Year */}
                    <div className="col-12 col-md-6 col-lg-4">
                      <div className="form-group mb-3">
                        <PrimaryInput
                          label="Total Leave Per Year"
                          type="number"
                          id="totalLeavePerYear"
                          placeholder="Enter total leave per year"
                          errors={errors?.totalLeavePerYear?.message}
                          {...register('totalLeavePerYear', {
                            required: 'This field is required',
                            valueAsNumber: true,
                            validate: (value) =>
                              value >= (totalLeavePerMonth || 0) * 12 ||
                              'Yearly leave must be greater than or equal to monthly leave times 12',
                          })}
                          min={state?.MonthValue ?? 0}
                          disabled={disabled}
                        />
                      </div>
                    </div>

                    <div className="row">
                      <div className="col-md-6">
                        <h4 className="fs-3 rajdhani-semibold my-4 border-bottom pb-3">
                          Yearly carry forward
                        </h4>
                        <div className="form-group mb-4">
                          <div className="form-check">
                            <input
                              className="form-check-input"
                              type="checkbox"
                              {...register('yearlyCarryForward')}
                            />
                            <label
                              className="form-check-label"
                              htmlFor="yearlyCarryForward"
                            >
                              Yes
                            </label>
                          </div>
                        </div>
                      </div>
                      <div className="col-md-6">
                        <h4 className="fs-3 rajdhani-semibold my-4 border-bottom pb-3">
                          Monthly carry forward
                        </h4>
                        <div className="form-group mb-4">
                          <div className="form-check">
                            <input
                              className="form-check-input"
                              type="checkbox"
                              {...register('monthlyCarryForward')}
                            />
                            <label
                              className="form-check-label"
                              htmlFor="yearlyCarryForward"
                            >
                              Yes
                            </label>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                  <FormButtons
                    isLoading={isLoading}
                    buttonStyle="gap-3 mt-4 mb-3"
                  >
                    {location?.state?.EditData
                      ? 'Edit Leave Configuration'
                      : 'Add Leave Configuration'}
                  </FormButtons>
                </div>
              </div>
            </CardLayout>
          </form>
        </div>
      </Layout>
    </>
  );
};

export default AddLeaveSetting;
